import createExternalStore from "../../../common/utils/createExternalStore";
import { getDecodedJwt, validToken } from "../../../helpers/jwt";
import { sleep } from "../../../helpers/sleep";
import type { AuthenticatedAccountUser } from "../../../types/management-auth";
import { logout, refresh } from "../../auth";
import {
    fetchAccountToken,
    fetchAccountUser,
    fetchExchangeToken,
    fetchPostPassword,
    fetchPutMfaSms,
    fetchPutMfaTotp,
} from "../../dinteroAuthApi";
import {
    getInspectToken,
    type InspectTokenEntry,
    removeInspectToken,
    setInspectToken,
} from "../../inspectTokens";
import { setCognitoSession, setLastAccountId } from "../../localStorage";
import type {
    AuthenticatedUser,
    ChangeMfaSmsModalStates,
    ChangeMfaTotpModalStates,
    ChangePasswordModalStates,
    CognitoSession,
    PartnerUser,
} from "../../types";
import {
    exchangeTokenExpiry,
    isExchangeToken,
    mintExchangeToken,
    refreshExchangeToken,
} from "../exchangeToken";
import { getAccountIdFromAudience } from "../helpers";

export interface State extends AuthenticatedUser {
    tokens: {
        [accountId: string]: string;
    };
    isLoadingTokens: boolean;
    isLoadingSubAccountTokens: boolean;
    exchangeTokenPartnerId: string | undefined;
    seq: number;
    changePasswordState: ChangePasswordModalStates;
    changeMfaSmsState: ChangeMfaSmsModalStates;
    changeMfaTotpState: ChangeMfaTotpModalStates;
    partner_user: PartnerUser | undefined;
}

const initialState: State = {
    cognito_access_token: "",
    exchangeTokenPartnerId: undefined,
    account_user: {
        id: "",
        accounts: [],
    },
    tokens: {},
    isLoadingTokens: false,
    isLoadingSubAccountTokens: false,
    seq: 0,
    changePasswordState: "pristine",
    changeMfaSmsState: "pristine",
    changeMfaTotpState: "pristine",
    partner_user: undefined,
};

const authStore = createExternalStore(
    initialState,
    {
        setAuthenticatedUser: (
            state,
            action: {
                accountUser: AuthenticatedUser;
                exchangeTokenPartnerId?: string;
            },
        ) => {
            logoutOnExpiry();
            return {
                ...state,
                ...action.accountUser,
                // Inspect mode is applied separately at bootstrap via
                // enterInspectFromEntry; a fresh auth starts in normal mode.
                partner_user: undefined,
                exchangeTokenPartnerId: undefined,
                isLoadingTokens: true,
            };
        },
        setCognitoSession: (
            state,
            action: {
                cognitoSession: CognitoSession;
            },
        ) => {
            // persist updated cognito session in local storage
            setCognitoSession(action.cognitoSession);
            return {
                ...state,
                ...action.cognitoSession,
            };
        },
        setPartnerSession: (
            state,
            action: {
                partner: PartnerUser;
            },
        ) => {
            return {
                ...state,
                partner_user: action.partner,
            };
        },
        setExchangeToken: (
            state,
            action: {
                token: string;
            },
        ) => {
            // Updates the in-memory exchange token only; the base cognito keys
            // in localStorage keep holding the real logged-in user.
            return {
                ...state,
                cognito_access_token: action.token,
            };
        },
        setInspectAuthenticatedUser: (
            state,
            action: {
                accountUser: AuthenticatedUser;
                exchangeTokenPartnerId: string;
            },
        ) => {
            return {
                ...state,
                ...action.accountUser,
                // The active session is the exchange token; its refresh token
                // and config belong to the partner, so keep them only on
                // `partner_user` to avoid refreshing into the partner identity.
                cognito_refresh_token: undefined,
                cognito_config: undefined,
                exchangeTokenPartnerId: action.exchangeTokenPartnerId,
                partner_user: { ...state },
                tokens: {},
            };
        },
        setLeaveInspect: (state) => {
            if (!state.partner_user) {
                return state;
            }
            // The base session was kept untouched in localStorage during
            // inspect, so we only restore it into memory here.
            return {
                ...state,
                ...state.partner_user,
                partner_user: undefined,
                exchangeTokenPartnerId: undefined,
            };
        },
        updateAuthenticatedUser: (
            state,
            action: {
                accountId: string;
            },
        ) => {
            refetchAuthenticatedUser(state, action.accountId);
            return {
                ...state,
            };
        },
        updateAuthenticatedUserResult: (
            state,
            action: {
                authenticatedUser: AuthenticatedUser;
                accountId: string;
            },
        ) => {
            refetchTokens(action.accountId);
            return {
                ...state,
                ...action.authenticatedUser,
            };
        },
        loadTokens: (
            state,
            action: {
                accountId: string;
                showLoading: boolean;
            },
        ) => {
            fetchAccountTokens(state, action.accountId);
            return {
                ...state,
                isLoadingTokens: action.showLoading,
                seq: state.seq + 1,
            };
        },
        loadTokensResponse: (
            state,
            action: {
                accounts: {
                    accountId: string;
                    token: string;
                }[];
            },
        ) => {
            return {
                ...state,
                isLoadingTokens: false,
                tokens: {
                    ...state.tokens,
                    ...action.accounts.reduce(
                        (acc, x) => ({ ...acc, [x.accountId]: x.token }),
                        {},
                    ),
                },
            };
        },
        loadSubAccountTokens: (
            state,
            action: {
                accountId: string;
                subAccountId: string;
            },
        ) => {
            fetchSubAccountTokens(action.accountId, action.subAccountId);
            return {
                ...state,
                isLoadingSubAccountTokens: true,
            };
        },
        loadSubAccountTokensResponse: (
            state,
            action: {
                accounts: {
                    accountId: string;
                    token: string;
                }[];
            },
        ) => {
            return {
                ...state,
                isLoadingSubAccountTokens: false,
                tokens: {
                    ...state.tokens,
                    ...action.accounts.reduce(
                        (acc, x) => ({ ...acc, [x.accountId]: x.token }),
                        {},
                    ),
                },
            };
        },
        openChangePassword: (state) => {
            return {
                ...state,
                changePasswordState: "pristine",
            };
        },
        changePassword: (
            state,
            action: {
                previous_password: string;
                proposed_password: string;
            },
        ) => {
            handleChangePassword(state, {
                previous_password: action.previous_password,
                proposed_password: action.proposed_password,
            });
            return {
                ...state,
                changePasswordState: "in-flight",
            };
        },
        changePasswordResponse: (
            state,
            action: {
                result: ChangePasswordModalStates;
            },
        ) => {
            return {
                ...state,
                changePasswordState: action.result,
            };
        },
        openAuthenticatedUserSettings: (state) => {
            return {
                ...state,
                changeMfaSmsState: "pristine",
                changeMfaTotpState: "pristine",
            };
        },
        changeAuthenticatedUserMfaSms: (
            state,
            action: {
                enabled: boolean;
                phone_number?: string;
            },
        ) => {
            handleChangeMfaSms(state, {
                enabled: action.enabled,
                phone_number: action.phone_number,
            });
            return {
                ...state,
                changeMfaSmsState: "in-flight",
            };
        },
        changeAuthenticatedUserMfaSmsResponse: (
            state,
            action: {
                result: ChangeMfaSmsModalStates;
            },
        ) => {
            return {
                ...state,
                changeMfaSmsState: action.result,
            };
        },
        changeAuthenticatedUserMfaTotp: (
            state,
            action: {
                enabled: boolean;
                user_code?: string;
            },
        ) => {
            handleChangeMfaTotp(state, {
                enabled: action.enabled,
                user_code: action.user_code,
            });
            return {
                ...state,
                changeMfaTotpState: "in-flight",
            };
        },
        changeAuthenticatedUserMfaTotpResponse: (
            state,
            action: {
                result: ChangeMfaTotpModalStates;
            },
        ) => {
            return {
                ...state,
                changeMfaTotpState: action.result,
            };
        },
        setState: (state, action: Partial<State>) => {
            return {
                ...state,
                ...action,
            };
        },
    },
    "AUTH",
);

const getAccountIdsForAccount = (
    accountUser: AuthenticatedAccountUser,
    accountId: string | undefined,
) => {
    const withoutPrefix = accountId?.substring(1);
    const matchingAccounts = accountUser.accounts.filter(
        (a) => a.account_id.substring(1) === withoutPrefix,
    );
    return matchingAccounts.map((a) => a.account_id);
};

export const getCognitoToken = async () => {
    // Get cognito token for current account.
    // If a user is inspecting, the token returned is the exchange token.
    const cognitoSession: CognitoSession = authStore.select((state) => ({
        cognito_access_token: state.cognito_access_token,
        cognito_refresh_token: state.cognito_refresh_token,
        cognito_config: state.cognito_config,
    }));
    const validCognitoToken = validToken(cognitoSession.cognito_access_token);
    if (validCognitoToken) {
        return validCognitoToken;
    }

    // The cognito access token is invalid or expired. If it is an inspect
    // exchange token, refresh it via the partner session it was minted from.
    const partnerSession = authStore.select((state) => state.partner_user);
    if (isExchangeToken(cognitoSession.cognito_access_token, partnerSession)) {
        const result = await refreshExchangeToken(
            cognitoSession.cognito_access_token,
            partnerSession as PartnerUser,
        );
        if (result) {
            if (result.partnerSession) {
                // base cognito token was refreshed: persist base keys + memory.
                // result.partnerSession carries only refreshed cognito fields,
                // so merge onto the full partner_user to keep account_user/tokens.
                setCognitoSession(result.partnerSession);
                authStore.dispatch("setPartnerSession", {
                    partner: {
                        ...(partnerSession as PartnerUser),
                        ...result.partnerSession,
                    },
                });
            }
            const { aud, exchanged } = getDecodedJwt(
                cognitoSession.cognito_access_token,
            );
            if (aud && exchanged) {
                const inspectedAccountId = getAccountIdFromAudience(aud);
                // write back only if the entry still exists (no repopulation)
                if (getInspectToken(inspectedAccountId)) {
                    setInspectToken(inspectedAccountId, {
                        token: result.accessToken,
                        exp: exchangeTokenExpiry(result.accessToken),
                        partnerId: exchanged.account_id,
                    });
                }
            }
            // update the in-memory exchange token only — never the base keys
            authStore.dispatch("setExchangeToken", {
                token: result.accessToken,
            });
            return result.accessToken;
        }
        // The exchange token could not be refreshed. Do NOT fall back to a
        // regular cognito refresh: the refresh token/config in the session
        // belong to the partner, not the inspected account, so using them
        // would silently swap the user onto their partner identity.
        logout(false);
        return;
    }

    // Update regular (not inspect) cognito token using the refresh token
    const updatedSession = await refresh(cognitoSession);
    if (updatedSession) {
        // persist updated session in authStore
        authStore.dispatch("setCognitoSession", {
            cognitoSession: updatedSession,
        });
        return updatedSession.cognito_access_token;
    }
    // log out user when we do not have a cognito token or a valid refresh token
    logout(false);
};

let isChecking = false;
const logoutOnExpiry = async () => {
    // only run one instance of the checker.
    if (isChecking) {
        return;
    }
    isChecking = true;
    // Checks every minute
    while (isChecking) {
        await sleep(60 * 1000);

        // getCognitoToken() logs out the user when we no longer have a valid cognito access token.
        const token = await getCognitoToken();
        if (!token) {
            // Reset expiry logger sot we can restart when
            isChecking = false;
        }
    }
};

const fetchAccountTokens = async (state: State, accountId: string) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const accountIds = getAccountIdsForAccount(
            state.account_user,
            accountId,
        );
        const promises = accountIds.map((id) =>
            fetchAccountToken(cognitoToken, id),
        );
        const responses = await Promise.all(promises);
        const accounts = responses.filter((r) => r) as {
            accountId: string;
            token: string;
        }[];
        setLastAccountId(accountId);
        authStore.dispatch("loadTokensResponse", {
            accounts,
        });
    }
};

const refetchAuthenticatedUser = async (state: State, accountId: string) => {
    // User has been updated so we fetch it again to get newest state
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const authenticatedAccountUser = await fetchAccountUser(cognitoToken);
        if (authenticatedAccountUser) {
            authStore.dispatch("updateAuthenticatedUserResult", {
                authenticatedUser: {
                    account_user: authenticatedAccountUser,
                } as AuthenticatedUser,
                accountId,
            });
        } else {
            // unable to authenticate user so we force a logout so the user can login again
            logout(false);
        }
    }
};

const refetchTokens = async (accountId: string) => {
    // TODO: figure out what this is needed for?
    return authStore.dispatch("loadTokens", {
        accountId,
        showLoading: false,
    });
};

const fetchSubAccountTokens = async (
    accountId: string,
    subAccountId: string,
) => {
    // When a partner is looking at details of a sub account from partner admin, but is not inspecting
    const exchangeTokenResponse = await fetchExchangeToken(
        accountId,
        subAccountId,
    );
    if (!exchangeTokenResponse) {
        return authStore.dispatch("loadSubAccountTokensResponse", {
            accounts: [],
        });
    }
    const nonPrefixedSubAccount = subAccountId.substring(1);
    const promises = ["P", "T"]
        .map((prefix) => `${prefix}${nonPrefixedSubAccount}`)
        .map((id) => fetchAccountToken(exchangeTokenResponse.access_token, id));
    const responses = await Promise.all(promises);
    const accounts = responses.filter((r) => r) as {
        accountId: string;
        token: string;
    }[];
    authStore.dispatch("loadSubAccountTokensResponse", {
        accounts,
    });
};

const handleChangePassword = async (
    state: State,
    payload: {
        previous_password: string;
        proposed_password: string;
    },
) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const response = await fetchPostPassword(cognitoToken, payload);
        switch (response.status) {
            case 201:
                return dispatchPasswordResult("success");
            case 400:
                return dispatchPasswordResult("wrong_password");
            case 401:
                return dispatchPasswordResult("error");
            case 403:
                return dispatchPasswordResult("error");
            case 429:
                return dispatchPasswordResult("rate_limit");
            case 500:
                return dispatchPasswordResult("error");
            default:
                return dispatchPasswordResult("error");
        }
    }
};

const dispatchPasswordResult = (
    result: "success" | "wrong_password" | "error" | "rate_limit",
) =>
    authStore.dispatch("changePasswordResponse", {
        result,
    });

const handleChangeMfaSms = async (
    state: State,
    payload: {
        enabled: boolean;
        phone_number?: string;
    },
) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const response = await fetchPutMfaSms(
            state.cognito_access_token,
            payload,
        );
        switch (response.status) {
            case 201:
                return dispatchMfaSmsResult("success");
            case 400:
                return dispatchMfaSmsResult("invalid_phone");
            case 401:
                return dispatchMfaSmsResult("error");
            case 403:
                return dispatchMfaSmsResult("error");
            case 429:
                return dispatchMfaSmsResult("rate_limit");
            case 500:
                return dispatchMfaSmsResult("error");
            default:
                return dispatchMfaSmsResult("error");
        }
    }
};

const dispatchMfaSmsResult = (
    result: "success" | "invalid_phone" | "error" | "rate_limit",
) =>
    authStore.dispatch("changeAuthenticatedUserMfaSmsResponse", {
        result,
    });

const handleChangeMfaTotp = async (
    state: State,
    payload: {
        enabled: boolean;
        user_code?: string;
    },
) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const response = await fetchPutMfaTotp(
            state.cognito_access_token,
            payload,
        );
        switch (response.status) {
            case 201:
                return dispsatchMfaTotpResult("success");
            case 400:
                return dispsatchMfaTotpResult("invalid_user_code");
            case 401:
                return dispsatchMfaTotpResult("error");
            case 403:
                return dispsatchMfaTotpResult("error");
            case 429:
                return dispsatchMfaTotpResult("rate_limit");
            case 500:
                return dispsatchMfaTotpResult("error");
            default:
                return dispsatchMfaTotpResult("error");
        }
    }
};

const dispsatchMfaTotpResult = (
    result: "success" | "invalid_user_code" | "error" | "rate_limit",
) =>
    authStore.dispatch("changeAuthenticatedUserMfaTotpResponse", {
        result,
    });

// External functions

/**
 * Return a valid cognito token for the partner account, minting it from the
 * partner's own cognito identity (held on `partner_user`) and refreshing that
 * identity first if it has expired. Used while inspecting: the active session
 * is the inspected account's exchange token, which can only authenticate as
 * that account — partner-scoped calls must use the partner's identity instead.
 */
const getPartnerCognitoToken = async (
    partnerUser: PartnerUser,
): Promise<string | undefined> => {
    const valid = validToken(partnerUser.cognito_access_token);
    if (valid) {
        return valid;
    }
    const updated = await refresh(partnerUser);
    if (updated) {
        // The base session in localStorage belongs to the partner during
        // inspect, so persist the refreshed cognito fields there and in memory.
        const refreshed = { ...partnerUser, ...updated };
        setCognitoSession(refreshed);
        authStore.dispatch("setPartnerSession", { partner: refreshed });
        return updated.cognito_access_token;
    }
    return undefined;
};

// Used by api.ts to get access token for a given API request
export const getAuthTokenForAccount = async (accountId: string) => {
    const { tokens, partnerId, partnerUser } = authStore.select((state) => ({
        tokens: state.tokens,
        partnerId: state.exchangeTokenPartnerId,
        partnerUser: state.partner_user,
    }));
    const tokenFromStore = validToken(tokens[accountId]);
    if (tokenFromStore) {
        return tokenFromStore;
    }
    // authStore token for the account is invalid or expired
    // check if cognito token is still valid

    // While inspecting, a request for the partner account itself (the partner
    // that minted the exchange token, in either live/test mode) must be authed
    // with the partner's cognito identity — the active exchange token only
    // authenticates as the inspected account and cannot mint a partner token.
    const isPartnerAccount =
        Boolean(partnerUser) &&
        Boolean(partnerId) &&
        accountId.substring(1) === partnerId?.substring(1);
    const cognitoToken = isPartnerAccount
        ? await getPartnerCognitoToken(partnerUser as PartnerUser)
        : await getCognitoToken();
    if (cognitoToken) {
        // reload account token using the cognito token
        const account = await fetchAccountToken(cognitoToken, accountId);
        if (account) {
            authStore.dispatch("loadTokensResponse", {
                accounts: [account],
            });
            return account.token;
        }
    }
};

export const inspectAccount = async (
    partnerId: string,
    inspectedAccountId: string,
) => {
    const baseSession: CognitoSession = authStore.select((state) => ({
        cognito_access_token: state.cognito_access_token,
        cognito_refresh_token: state.cognito_refresh_token,
        cognito_config: state.cognito_config,
    }));
    const minted = await mintExchangeToken(
        partnerId,
        inspectedAccountId,
        baseSession,
    );
    if (!minted) {
        return;
    }
    if (minted.partnerSession) {
        // base cognito token was refreshed while minting — persist it
        setCognitoSession(minted.partnerSession);
    }
    const token = minted.accessToken;
    const inspectedUser = await fetchAccountUser(token);
    if (!inspectedUser) {
        return;
    }
    setInspectToken(inspectedAccountId, {
        token,
        exp: exchangeTokenExpiry(token),
        partnerId,
    });
    authStore.dispatch("setInspectAuthenticatedUser", {
        accountUser: {
            cognito_access_token: token,
            account_user: inspectedUser,
        },
        exchangeTokenPartnerId: partnerId,
    });
};

/**
 * Bootstrap a tab into inspect mode from a stored map entry. Assumes the store
 * already holds the base/partner session (so `partner_user` is captured from it).
 * Returns false (and drops the entry) when the token cannot be restored.
 */
export const enterInspectFromEntry = async (
    inspectedAccountId: string,
    entry: InspectTokenEntry,
): Promise<boolean> => {
    const baseSession: CognitoSession = authStore.select((state) => ({
        cognito_access_token: state.cognito_access_token,
        cognito_refresh_token: state.cognito_refresh_token,
        cognito_config: state.cognito_config,
    }));

    let token = validToken(entry.token);
    if (!token) {
        const minted = await mintExchangeToken(
            entry.partnerId,
            inspectedAccountId,
            baseSession,
        );
        if (!minted) {
            removeInspectToken(inspectedAccountId);
            return false;
        }
        if (minted.partnerSession) {
            setCognitoSession(minted.partnerSession);
        }
        token = minted.accessToken;
        setInspectToken(inspectedAccountId, {
            token,
            exp: exchangeTokenExpiry(token),
            partnerId: entry.partnerId,
        });
    }

    const inspectedUser = await fetchAccountUser(token);
    if (!inspectedUser) {
        removeInspectToken(inspectedAccountId);
        return false;
    }

    authStore.dispatch("setInspectAuthenticatedUser", {
        accountUser: {
            cognito_access_token: token,
            account_user: inspectedUser,
        },
        exchangeTokenPartnerId: entry.partnerId,
    });
    return true;
};

export const leaveInspect = () => {
    const { cognito_access_token, exchangeTokenPartnerId } = authStore.select(
        (state) => ({
            cognito_access_token: state.cognito_access_token,
            exchangeTokenPartnerId: state.exchangeTokenPartnerId,
        }),
    );
    if (exchangeTokenPartnerId) {
        const { aud } = getDecodedJwt(cognito_access_token);
        if (aud) {
            removeInspectToken(getAccountIdFromAudience(aud));
        }
    }
    authStore.dispatch("setLeaveInspect");
};

export default authStore;
